import { DestroyRef, Injectable, NgZone } from "@angular/core";
import posthog, {CaptureOptions, EventName, Properties} from 'posthog-js';
import { WebmateOptions } from '@webmate-web-core/webmate-options';
import { UserService } from '@webmate-web-core/shared/services/user.service';
import {FeatureService} from './feature.service';
import {isDefined} from '@webmate-web-core/shared/utils/utils';
import {filter, switchMap, take} from 'rxjs/operators';


@Injectable({
    providedIn: "root"
})
export class PosthogService {
    private initialized = false;
    private isOptedOut = false;
    private enabled = false;

    constructor(
        private ngZone: NgZone,
        private options: WebmateOptions,
        private userService: UserService,
        private featureService: FeatureService
    ) {
        // nothing here
    }


    public init(): void {
        if (this.initialized || !this.options.analyticsPosthogEnabled) {
            return;
        }

        this.initialized = true;

        this.userService.getCurrentUserData().pipe(
            filter(user => !!user.publicProperties['trackingConsent']),
            switchMap(() => this.featureService.getFeature('webmate.tracking-consent')),
            filter(feature => isDefined(feature) && feature.isActive),
            take(1)
        ).subscribe(() => {
            this.ngZone.runOutsideAngular(() => {
                posthog.init(this.options.analyticsPosthogKey, {
                    api_host: this.options.analyticsPosthogUrl,
                    defaults: '2026-01-30',
                });

                this.enabled = true;
            });
        });
    }


    public capture(event: EventName, properties?: Properties | null, options?: CaptureOptions): void {
        this.userService.getCurrentUserData().pipe(
            filter(user => !!user.publicProperties['trackingConsent']),
            switchMap(() => this.featureService.getFeature('webmate.tracking-consent')),
            filter(feature => isDefined(feature) && feature.isActive),
            take(1)
        ).subscribe(() => {
            posthog.capture(event, properties, options);
        });
    }


    public disableTracking(): void {
        posthog.opt_out_capturing();
        this.isOptedOut = true;
        this.enabled = false;
    }


    public enableTracking(): void {
        if (!this.isOptedOut) {
            return;
        }

        posthog.opt_in_capturing();

        this.isOptedOut = false;
        this.enabled = true;
    }


    public get isEnabled() :boolean {
        return this.enabled;
    }


    public get sessionId() {
        return posthog.get_session_id();
    }


    public get distinctId() {
        return posthog.get_distinct_id();
    }
}